import { AxiosError } from "axios";
import { ApiService } from "./api.service";
import { BASE_URL } from "./index";
import {
  CreateUserModel,
  UserDetailsModel,
  UserListModel,
} from "../models/users/users.model";
import { IResponse } from "../models/response";
import { useMutation, useQuery } from "@tanstack/react-query";
import { PasswordModel } from "../models/password/password.model";
import { ErrorTypeModel } from "../models/error/error.model";
import { InfoModel } from "../models/info.model";

/**
 * Hook to fetch a paginated list of users.
 *
 * @param {number} per_page - Number of users per page.
 * @param {number} page - Current page number.
 * @param {Record<string, unknown>} [filters] - Optional filters to apply.
 * @param {Record<string, unknown>} [sort] - Optional sorting options.
 * @param {0 | 1} [pagination] - Whether to enable (1) or disable (0) pagination.
 * @returns {Object} React Query object with users list and a `refetch` function.
 */
export const useFetchUsers = (
  per_page: number,
  page: number,
  query: string = "",
  filters?: Record<string, unknown>,
  sort?: Record<string, unknown>,
  pagination?: 0 | 1
) => {
  const queryKey = ["users", per_page, page, query, JSON.stringify(filters), sort];
  const { refetch, ...queryResult } = useQuery<
    IResponse<UserListModel[]>,
    AxiosError
  >(queryKey, async () => {
    const params = {
      per_page: per_page,
      page: page,
      query: query,
      sortField: sort?.field,
      sortDirection: sort?.sort,
      pagination: pagination,
      ...filters,
    };

    const userData = await ApiService.get("/users", { params });
    return userData?.data;
  });
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch users without pagination.
 *
 * @returns {Object} React Query object with users list and a `refetch` function.
 */
export const useFetchUsersWithoutPagination = (
) => {
  const queryKey = ["users-without-pagination"];
  const { refetch, ...queryResult } = useQuery<
    UserListModel[],
    AxiosError
  >(queryKey, async () => {
    const params = {
      pagination: 0,
    };

    const userData = await ApiService.get("/users", { params });
    return userData?.data;
  });
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch the details of a single user.
 *
 * @param {string} [id] - User ID.
 * @returns {Object} React Query object with user details and a `refetch` function.
 */
export const useFetchUser = (id?: string) => {
  const queryKey = ["users", id];
  const { refetch, ...queryResult } = useQuery<
    IResponse<UserDetailsModel>,
    AxiosError
  >(queryKey, () => ApiService.get(`${BASE_URL}/users/${id}`), {
    staleTime: 60 * 1000,
    enabled: Boolean(id),
  });
  return { ...queryResult, refetch };
};

/**
 * Hook to create a new user.
 *
 * @returns {Object} React Query mutation object for creating a user.
 */
export const useCreateUser = () => {
  const createUser = useMutation<
    IResponse<CreateUserModel>,
    AxiosError<ErrorTypeModel>,
    FormData
  >(async (postData: FormData) => {
    const response = await ApiService.postFormData(`/users`, postData);
    return response.data;
  });
  return createUser;
};

/**
 * Hook to update an existing user.
 *
 * @param {string} [id] - User ID.
 * @returns {Object} React Query mutation object for updating a user.
 */
export const useUpdateUser = (id?: string) => {
  const updateUser = useMutation<
    IResponse<CreateUserModel>,
    AxiosError<ErrorTypeModel>,
    FormData
  >(async (updatedData: FormData) => {
    const response = await ApiService.postFormData(`/users/${id}?_method=PATCH`, updatedData);
    return response.data;
  });

  return updateUser;
};

/**
 * Hook to change a specific user's password (admin action).
 *
 * @param {string} [id] - User ID whose password will be changed.
 * @returns {Object} React Query mutation object for changing a user's password.
 */
export const useChangePassword = (id?: string) => {
  const changePassword = useMutation<
    IResponse<PasswordModel>,
    AxiosError<ErrorTypeModel>,
    PasswordModel
  >(async (updatedData: PasswordModel) => {
    const response = await ApiService.patch(
      `/users/${id}/change-password`,
      updatedData
    );
    return response.data;
  });
  return changePassword;
};

/**
 * Hook to change the currently logged-in user's password (self-service).
 *
 * @returns {Object} React Query mutation object for changing your own password.
 */
export const useChangeMyPassword = () => {
  const changePassword = useMutation<
    IResponse<PasswordModel>,
    AxiosError<ErrorTypeModel>,
    PasswordModel
  >(async (updatedData: PasswordModel) => {
    const response = await ApiService.patch(
      `/auth/change-password`,
      updatedData
    );
    return response.data;
  });
  return changePassword;
};

/**
 * Hook to fetch the template types for a specific user.
 *
 * @param {string} [id] - User ID.
 * @returns {Object} React Query object with template types and a `refetch` function.
 */
export const useFetchUserTemplateTypes = (id?: string) => {
  const queryKey = ["users", id, "template-types"];
  const { refetch, ...queryResult } = useQuery<
    IResponse<InfoModel[]>,
    AxiosError
  >(queryKey, () => ApiService.get(`${BASE_URL}/users/${id}/template-types`));
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch task reports to.
 *
 * @returns {Object} React Query object with task reports to and a `refetch` function.
 */
export const useFetchTaskReportsTo = () => {
  const queryKey = ["task-reports-to"];
  const { refetch, ...queryResult } = useQuery<
    IResponse<InfoModel[]>,
    AxiosError
  >(queryKey, () => ApiService.get(`/get-task-reports-to`));
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch responsible users.
 *
 * @returns {Object} React Query object with responsible users and a `refetch` function.
 */
export const useFetchResponsibleUsers = () => {
  const queryKey = ["responsible-users"];
  const { refetch, ...queryResult } = useQuery<
    IResponse<InfoModel[]>,
    AxiosError
  >(queryKey, () => ApiService.get(`/get-responsible-users`));
  return { ...queryResult, refetch };
};
